export interface MermaidDiagramConfig {
  definition: string;
  description?: string;
}

export const mermaidDiagrams: Record<string, MermaidDiagramConfig> = {
  'park-between-actions': {
    definition: `%%{init: {'themeVariables': {'fontSize': '34px'}, 'flowchart': {'nodeSpacing': 70, 'rankSpacing': 110, 'padding': 25}}}%%
flowchart LR
    Act[Agent acts<br/>one input] --> Park[Park<br/>snapshot VM,<br/>release host slot]
    Park --> Think[Model thinks<br/>host slot is free]
    Think --> Wake[Wake<br/>restore in 130-210ms]
    Wake --> Act2[Agent acts again]
    Act2 -.->|repeat every turn| Park

    classDef act fill:#BBDEFB,stroke:#1565C0,stroke-width:3px,color:#000;
    classDef park fill:#D1C4E9,stroke:#512DA8,stroke-width:3px,color:#000;
    classDef think fill:#FFCCBC,stroke:#D84315,stroke-width:3px,color:#000;
    class Act,Act2 act;
    class Park,Wake park;
    class Think think;

    linkStyle 0,1,2,3 stroke:#757575,stroke-width:5px
    linkStyle 4 stroke:#757575,stroke-width:5px,stroke-dasharray: 12 8`,
    description: 'The park-between-actions loop: the desktop is snapshotted and its host slot released while the model thinks, then restored just before the next action.'
  },
  'durable-wake-path': {
    definition: `%%{init: {'themeVariables': {'fontSize': '34px'}, 'flowchart': {'nodeSpacing': 60, 'rankSpacing': 95, 'padding': 25}}}%%
flowchart LR
    Live[Live desktop] --> Diff[Diff snapshot]
    Diff --> S3[(Object storage<br/>~220MB package)]
    S3 --> Get[Ranged GET<br/>223MB in ~2.5s]
    Get --> Ext[Extract once]
    Ext --> Restore[Restore<br/>wake p99 3.4s]

    classDef live fill:#BBDEFB,stroke:#1565C0,stroke-width:3px,color:#000;
    classDef store fill:#FFCCBC,stroke:#D84315,stroke-width:3px,color:#000;
    classDef step fill:#FAFAFA,stroke:#424242,stroke-width:2px,color:#000;
    class Live,Restore live;
    class S3 store;
    class Diff,Get,Ext step;

    linkStyle 0,1,2,3,4 stroke:#757575,stroke-width:5px`,
    description: 'Parking to durable object storage and waking from it: a diff snapshot is demoted to S3, then a ranged GET plus one local extract serves repeated sub-5-second wakes.'
  },
  'park-continuity': {
    definition: `%%{init: {'themeVariables': {'fontSize': '34px'}, 'flowchart': {'nodeSpacing': 55, 'rankSpacing': 120, 'padding': 25, 'wrappingWidth': 900}}}%%
flowchart LR
    Park["Park and wake<br/>n=50 · idle 1 min and 10 min"] --> Survive["SURVIVES 50/50<br/>cookies · TLS sessions · websockets<br/>downloads · timers · SSH control · fetches"]
    Park --> Dies["DIES 50/50<br/>X clipboard"]

    classDef park fill:#D1C4E9,stroke:#512DA8,stroke-width:3px,color:#000;
    classDef ok fill:#C8E6C9,stroke:#2E7D32,stroke-width:3px,color:#000;
    classDef bad fill:#FFCDD2,stroke:#C62828,stroke-width:3px,color:#000;
    class Park park;
    class Survive ok;
    class Dies bad;

    linkStyle 0,1 stroke:#757575,stroke-width:5px`,
    description: 'Measured continuity across a park: everything session-shaped survives, and the X clipboard fails every trial because its contents are owned by a live process rather than stored as data.'
  },
  'fork-fanout': {
    definition: `%%{init: {'themeVariables': {'fontSize': '34px'}, 'flowchart': {'nodeSpacing': 45, 'rankSpacing': 110, 'padding': 25}}}%%
flowchart TB
    Parent[Live desktop<br/>4 GiB, running] --> C1[fork<br/>0ms · 56 KiB]
    Parent --> C2[fork<br/>0ms · 56 KiB]
    Parent --> C3[fork<br/>0ms · 56 KiB]
    Parent --> Cn[up to 50 branches<br/>186ms total]

    classDef parent fill:#D1C4E9,stroke:#512DA8,stroke-width:3px,color:#000;
    classDef child fill:#BBDEFB,stroke:#1565C0,stroke-width:2px,color:#000;
    class Parent parent;
    class C1,C2,C3,Cn child;

    linkStyle 0,1,2,3 stroke:#757575,stroke-width:5px`,
    description: 'Copy-on-write forking: each child shares the parent 4 GiB image and pays only for what it dirties, so fifty branches materialize in 186 ms.'
  },
  'harness-agent-surface': {
    definition: `%%{init: {'themeVariables': {'fontSize': '28px'}, 'flowchart': {'nodeSpacing': 55, 'rankSpacing': 85, 'padding': 22}}}%%
flowchart LR
    Agent[Agent] --> CLI[CLI discovery<br/>and commands]
    CLI --> Bridge[Local bridge<br/>structured request]
    Bridge --> Native[Application-native<br/>action map]
    Native --> App[GIMP, Kdenlive,<br/>or Blender]
    App --> Artifact[Native editable<br/>artifact]
    Artifact -.->|inspect and revise| Agent

    classDef agent fill:#8baaad,stroke:#1c3738,stroke-width:3px,color:#000f08;
    classDef system fill:#1c3738,stroke:#000f08,stroke-width:3px,color:#f4fff8;
    classDef surface fill:#f4fff8,stroke:#4d4847,stroke-width:2px,color:#000f08;
    class Agent agent;
    class Bridge,Native system;
    class CLI,App,Artifact surface;
    linkStyle default stroke:#4d4847,stroke-width:4px`,
    description: 'Harness maps a discoverable CLI command through a localhost bridge to the application\'s existing native action model, then returns a normal editable artifact.'
  },
  'harness-review-loop': {
    definition: `%%{init: {'themeVariables': {'fontSize': '28px'}, 'flowchart': {'nodeSpacing': 55, 'rankSpacing': 80, 'padding': 22}}}%%
flowchart LR
    Prompt[Editorial goal] --> Agent[Agent builds<br/>first pass]
    Agent --> Project[Native project<br/>and render]
    Project --> Review[Human reviews<br/>file and timestamp]
    Review --> Feedback[Structured feedback]
    Feedback --> Agent
    Project --> Handoff[Editor takes over]

    classDef agent fill:#8baaad,stroke:#1c3738,stroke-width:3px,color:#000f08;
    classDef system fill:#1c3738,stroke:#000f08,stroke-width:3px,color:#f4fff8;
    classDef surface fill:#f4fff8,stroke:#4d4847,stroke-width:2px,color:#000f08;
    class Agent agent;
    class Project,Feedback system;
    class Prompt,Review,Handoff surface;
    linkStyle default stroke:#4d4847,stroke-width:4px`,
    description: 'Studio closes the loop around Harness: an agent produces a native first pass, a person reviews the actual media, and revisions remain editable in the original application.'
  },
  'boostos-system-layer': {
    definition: `%%{init: {'themeVariables': {'fontSize': '27px'}, 'flowchart': {'nodeSpacing': 48, 'rankSpacing': 75, 'padding': 22}}}%%
flowchart TB
    TUI[TUI] --> Layer[BoostOS shared<br/>agent surface]
    IDE[GUI or IDE] --> Layer
    Harness[Agent harness] --> Layer
    Layer --> Search[Semantic and<br/>trigram search]
    Layer --> Facts[JSON process,<br/>port, and resource facts]
    Layer --> Registry[Agent and<br/>session registry]
    Layer --> Usage[Usage proxy<br/>and accounting]
    Search --> Linux[Linux filesystem,<br/>processes, and network]
    Facts --> Linux
    Registry --> Linux
    Usage --> Linux

    classDef client fill:#f4fff8,stroke:#4d4847,stroke-width:2px,color:#000f08;
    classDef layer fill:#8baaad,stroke:#1c3738,stroke-width:3px,color:#000f08;
    classDef service fill:#1c3738,stroke:#000f08,stroke-width:3px,color:#f4fff8;
    class TUI,IDE,Harness client;
    class Layer layer;
    class Search,Facts,Registry,Usage service;
    class Linux client;
    linkStyle default stroke:#4d4847,stroke-width:4px`,
    description: 'Editors, terminals, and agent harnesses share the same search, system-state, registry, and accounting primitives instead of rebuilding them inside each app.'
  },
  'piper-working-set': {
    definition: `%%{init: {'themeVariables': {'fontSize': '27px'}, 'flowchart': {'nodeSpacing': 48, 'rankSpacing': 78, 'padding': 22}}}%%
flowchart LR
    History[Full event history] --> Protect[Protect system,<br/>user, and recent work]
    Protect --> Tools[Clear stale<br/>tool payloads]
    Tools --> Decay[Decay older<br/>eligible messages]
    Decay --> Keys[Replace completed<br/>phases with keys]
    Keys --> Working[Bounded<br/>working set]
    Archive[(Phase archive)] -.->|relevant recall| Working
    Working --> Model[Next model turn]

    classDef history fill:#f4fff8,stroke:#4d4847,stroke-width:2px,color:#000f08;
    classDef policy fill:#1c3738,stroke:#000f08,stroke-width:3px,color:#f4fff8;
    classDef focus fill:#8baaad,stroke:#1c3738,stroke-width:3px,color:#000f08;
    class History,Archive history;
    class Protect,Tools,Decay,Keys policy;
    class Working,Model focus;
    linkStyle default stroke:#4d4847,stroke-width:4px`,
    description: 'The complete Piper design builds a fresh working set for every turn, preserving recent evidence while progressively reducing older context and recalling archived phases when relevant.'
  },
  'piper-pressure-policy': {
    definition: `%%{init: {'themeVariables': {'fontSize': '28px'}, 'flowchart': {'nodeSpacing': 45, 'rankSpacing': 75, 'padding': 22}}}%%
flowchart LR
    Raw[Below 60 percent<br/>pass through raw] --> Micro[At 60 percent<br/>microcompact old tools]
    Micro --> Shield[Keep recent 80 percent<br/>and latest 8 tools raw]
    Shield --> Decay[At 92 percent<br/>late tool-only decay]
    Decay --> Emergency[At 98 percent<br/>tighter emergency target]

    classDef safe fill:#f4fff8,stroke:#4d4847,stroke-width:2px,color:#000f08;
    classDef active fill:#8baaad,stroke:#1c3738,stroke-width:3px,color:#000f08;
    classDef pressure fill:#1c3738,stroke:#000f08,stroke-width:3px,color:#f4fff8;
    class Raw,Shield safe;
    class Micro active;
    class Decay,Emergency pressure;
    linkStyle default stroke:#4d4847,stroke-width:4px`,
    description: 'The benchmark-safe policy is intentionally reluctant: short runs remain raw, stale tool output goes first, and more destructive decay starts only near the limit.'
  },
  'assistant-scope-explosion': {
    definition: `%%{init: {'themeVariables': {'fontSize': '28px'}, 'flowchart': {'nodeSpacing': 52, 'rankSpacing': 80, 'padding': 22}}}%%
flowchart TB
    Assistant[One personal assistant] --> Harness[Agent harness]
    Assistant --> Memory[Memory system]
    Assistant --> Cloud[Cloud hosting]
    Assistant --> Mobile[Mobile application]
    Assistant --> Integrations[Email, calendar,<br/>Drive, and location]
    Harness --> Burden[One-person<br/>maintenance surface]
    Memory --> Burden
    Cloud --> Burden
    Mobile --> Burden
    Integrations --> Burden

    classDef focus fill:#8baaad,stroke:#1c3738,stroke-width:3px,color:#000f08;
    classDef system fill:#f4fff8,stroke:#4d4847,stroke-width:2px,color:#000f08;
    classDef burden fill:#1c3738,stroke:#000f08,stroke-width:3px,color:#f4fff8;
    class Assistant focus;
    class Harness,Memory,Cloud,Mobile,Integrations system;
    class Burden burden;
    linkStyle default stroke:#4d4847,stroke-width:4px`,
    description: 'The assistant became five infrastructure projects at once; every useful feature expanded the maintenance surface around the part that was actually personal.'
  },
  'desktop-agent-sensor-fusion': {
    definition: `%%{init: {'themeVariables': {'fontSize': '28px'}, 'flowchart': {'nodeSpacing': 55, 'rankSpacing': 82, 'padding': 22}}}%%
flowchart LR
    Screen[Screenshot<br/>pixels] --> State[Combined interface<br/>state]
    OCR[OCR<br/>visible text] --> State
    A11y[Accessibility tree<br/>semantic controls] --> State
    UIA[Windows UI Automation<br/>native elements] --> State
    State --> Native[Use strongest<br/>native action]
    Native --> Fallback[Coordinate fallback<br/>only when needed]
    Fallback --> Verify[Observe and verify<br/>the new state]

    classDef input fill:#f4fff8,stroke:#4d4847,stroke-width:2px,color:#000f08;
    classDef focus fill:#8baaad,stroke:#1c3738,stroke-width:3px,color:#000f08;
    classDef action fill:#1c3738,stroke:#000f08,stroke-width:3px,color:#f4fff8;
    class Screen,OCR,A11y,UIA input;
    class State focus;
    class Native,Fallback,Verify action;
    linkStyle default stroke:#4d4847,stroke-width:4px`,
    description: 'Desktop Agent combines pixels, text, accessibility metadata, and native automation, then chooses the strongest available interaction path and verifies the result.'
  },
  'assistant-architecture-2': {
    definition: `%%{init: {'themeVariables': {'fontSize': '50px'}, 'flowchart': {'nodeSpacing': 80, 'rankSpacing': 200, 'padding': 30}}}%%
flowchart LR
    %% Horizontal layout

    User[Mobile interface]

    subgraph API [API Container]
        direction TB
        API_Endpoint[API Endpoint<br/>FastAPI]
        Supervisor[Supervisor<br/>Agent]
        SSE[SSE Stream]
    end

    Redis[(Redis<br/>Broker & Backend)]

    subgraph Worker ["WorkerContainer"]
        direction TB
        Worker_Process[Worker Process]
        Agents[Specialized Sub-Agent]
        Worker_Process -->|Spawn| Agents
        Agents -.->|Result| Worker_Process

    end

    %% Styling - Refined Professional Palette
    classDef apiContainer fill:#BBDEFB,stroke:#1565C0,stroke-width:3px,color:#000;
    classDef workerContainer fill:#D1C4E9,stroke:#512DA8,stroke-width:3px,color:#000;
    classDef infrastructure fill:#FFCCBC,stroke:#D84315,stroke-width:3px,color:#000;
    classDef component fill:#FAFAFA,stroke:#424242,stroke-width:2px,color:#000;
    classDef user fill:#B3E5FC,stroke:#0288D1,stroke-width:2px,color:#000;

    class API apiContainer;
    class Worker workerContainer;
    class Redis infrastructure;
    class API_Endpoint,Supervisor,SSE,Worker_Process,Agents component;
    class User user;

    %% Flows
    User -->|Query/Context | API_Endpoint
    API_Endpoint -->|Evaluate| Supervisor

    Supervisor -->|Quick Reply| User
    Supervisor -->|Delegate| Redis

    Redis -->|Pop| Worker_Process

    Worker_Process -.->|Status| Redis
    Redis -.->|Stream| SSE
    SSE -.->|Notify| User

    %% Edge Styling - Simplified arrows
    linkStyle 0 stroke:#757575,stroke-width:6px
    linkStyle 1 stroke:#757575,stroke-width:6px,stroke-dasharray: 15 10
    linkStyle 2 stroke:#757575,stroke-width:6px
    linkStyle 3 stroke:#757575,stroke-width:6px
    linkStyle 4 stroke:#757575,stroke-width:6px
    linkStyle 5 stroke:#757575,stroke-width:6px
    linkStyle 6 stroke:#757575,stroke-width:6px
    linkStyle 7 stroke:#757575,stroke-width:6px,stroke-dasharray: 15 10
    linkStyle 8 stroke:#757575,stroke-width:6px,stroke-dasharray: 15 10
    linkStyle 9 stroke:#757575,stroke-width:6px,stroke-dasharray: 15 10`,
    description: 'New architecture of the personal assistant showing the API container with supervisor agent, Redis broker for task delegation, worker containers with specialized sub-agents.'
  },
  'evaluator-optimizer-workflow': {
    definition: `%%{init: {'themeVariables': {'fontSize': '40px'}}}%%
flowchart LR
    Start[Query] --> Generate[Initial Response]
    Generate --> Overseer[Evaluator Model]
    Overseer --> Evaluate{Satisfactory?}
    
    Evaluate -->|Yes| Output[Return Output]
    Evaluate -->|No| Clarify{Clarification needed?}
    
    Clarify -->|Yes| Prompt[Prompt User]
    Prompt --> Regenerate1[Regenerate Response]
    Regenerate1 -.-> Overseer
    
    Clarify -->|No| External{External data needed?}
    
    External -->|Yes| AskPermission[Fetch data]
    AskPermission --> PermissionCheck{Sufficient data?}
    
    PermissionCheck -->|Yes| Regenerate2[Regenerate Response]
    Regenerate2 -.-> Overseer
    
    PermissionCheck -->|No| External
    
    External -->|No| FetchData[Use feedback]
    FetchData --> Regenerate3[Regenerate Response]
    Regenerate3 -.-> Overseer
    
    %% Styling
    classDef userAction fill:#dae8fc,stroke:#6c8ebf,stroke-width:1.5px,color:#000;
    classDef process fill:#ffe6cc,stroke:#d79b00,stroke-width:1.5px,color:#000;
    classDef decision fill:#d5e8d4,stroke:#82b366,stroke-width:1.5px,color:#000;
    classDef output fill:#dae8fc,stroke:#6c8ebf,stroke-width:1.5px,color:#000;
    
    class Start,Prompt,AskPermission,Output userAction;
    class Generate,Overseer,Regenerate1,Regenerate2,Regenerate3,FetchData process;
    class Evaluate,Clarify,External,PermissionCheck decision;
    class Output output;
    
    %% Edge Styling - Make arrows more visible
    linkStyle 0 stroke:#f5f5f5,stroke-width:10px
    linkStyle 1 stroke:#f5f5f5,stroke-width:10px
    linkStyle 2 stroke:#f5f5f5,stroke-width:10px
    linkStyle 3 stroke:#f5f5f5,stroke-width:10px
    linkStyle 4 stroke:#f5f5f5,stroke-width:10px
    linkStyle 5 stroke:#f5f5f5,stroke-width:10px
    linkStyle 6 stroke:#f5f5f5,stroke-width:10px
    linkStyle 7 stroke:#f5f5f5,stroke-width:10px,stroke-dasharray: 20 10
    linkStyle 8 stroke:#f5f5f5,stroke-width:10px
    linkStyle 9 stroke:#f5f5f5,stroke-width:10px
    linkStyle 10 stroke:#f5f5f5,stroke-width:10px
    linkStyle 11 stroke:#f5f5f5,stroke-width:10px
    linkStyle 12 stroke:#f5f5f5,stroke-width:10px,stroke-dasharray: 20 10
    linkStyle 13 stroke:#f5f5f5,stroke-width:10px
    linkStyle 14 stroke:#f5f5f5,stroke-width:10px
    linkStyle 15 stroke:#f5f5f5,stroke-width:10px
    linkStyle 16 stroke:#f5f5f5,stroke-width:10px,stroke-dasharray: 20 10`,
    description: 'Workflow showing how the assistant evaluates responses, requests clarification or external data when needed, and regenerates responses until satisfactory.'
  }
};
